/**
 * The runtime flame (q-npc-props-adoption-and-flame; owner 2026-10-19): one reusable,
 * data-driven, animated flame for every torch, brazier, sconce and hearth, replacing the
 * static flame primitives baked into the models.
 *
 * What it is:
 * - `FlameMesh` — one camera-facing billboard per emitter: a teardrop with a hot core
 *   fading through orange to a red tip, flickering in height, sway and brightness, drawn
 *   ADDITIVELY without depth writes. Its geometry is a DEGENERATE quad (all four positions at
 *   the origin; the shader builds the corners from `uv`), so any pass that draws it with an
 *   override material — the SSAO normal pass, a shadow map — rasterises nothing. It casts
 *   no shadow, ignores raycasts and is named + flagged so the static merge and the scatter
 *   instancer keep it live (`behaviourReason`: name `…emitter`, `userData.animated`).
 * - Its size is the emitter's `size` times the WORLD scale of its parent, so a flame on a
 *   held torch scaled under the rig's centimetre armature still measures in metres. Every
 *   flame gets its own flicker phase from its world position — a row of wall torches never
 *   pulses in unison.
 * - `FlameSystem` — owns the ONE shared geometry + material (per renderer), advances their
 *   clock, fades flames into the scene fog, and lends a small pool of flickering point
 *   lights to the nearest LIT flames drawn last frame. The pool is created once at its full
 *   size (lights never come and go, which would recompile every lit material); the low
 *   profile gets no pool at all (flameConfig.ts).
 *
 * The WebGL2 fallback uses the GLSL `ShaderMaterial` below; the WebGPU default passes the TSL
 * sibling (flameNodeMaterial.ts) through the same `FlameMaterialHandle` seam, so this file
 * never imports `three/webgpu`.
 *
 * Import boundary (AD-122): frontend-local; `three` is allowed in frontend/.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  Mesh,
  PointLight,
  ShaderMaterial,
  Sphere,
  UniformsLib,
  UniformsUtils,
  Vector3,
  type Camera,
  type Fog,
  type FogExp2,
  type Material,
  type Object3D,
  type Raycaster,
  type Intersection,
} from 'three';

import type { AssetEmitter } from '@great-cloud/shared/client';

import { markSharedResource } from '../assets/gpuOwnership.js';
import type { FlameConfig, FlameDetail } from './flameConfig.js';

/** Object name of every flame (matches staticMerge's `emitter` behaviour name). */
export const FLAME_OBJECT_NAME = 'gc-flame-emitter';

/** Flame colours, linear RGB: white-hot core → orange body → red tip. */
export const FLAME_CORE: readonly [number, number, number] = [1.0, 0.82, 0.45];
export const FLAME_BODY: readonly [number, number, number] = [1.0, 0.42, 0.07];
export const FLAME_TIP: readonly [number, number, number] = [0.75, 0.1, 0.02];
/** Brightness multiplier: above 1 at the core, so the desktop bloom picks the flame up. */
export const FLAME_INTENSITY = 1.6;
/** Billboard width as a fraction of its height. */
export const FLAME_ASPECT = 0.55;

/**
 * The flame's shape and motion — ONE table both siblings read: the GLSL below is generated
 * from it and flameNodeMaterial.ts builds its graph from it, so the two cannot drift
 * (q-webgpu-grass-and-flame-parity).
 *
 * Every falloff is written as `1 − smoothstep(lo, hi, v)` with `lo < hi`. The first cut used
 * reversed edges (`smoothstep(1.0, 0.5, d)`): GLSL calls that undefined and WGSL an
 * indeterminate value. ANGLE happened to give the Hermite ramp; the WebGPU path drew a
 * pale, hard-edged cone. The forward form is the same curve and is defined on both.
 */
export const FLAME_SHAPE = {
  /** Height flicker: 1 + heightAmp·sin(t·heightFreq + φ) (+ the second octave on full). */
  heightAmp: 0.12,
  heightFreq: 7.3,
  heightAmp2: 0.07,
  heightFreq2: 13.1,
  heightPhase2: 1.7,
  /** Sideways sway of the upper flame (× uv.y²). */
  swayAmp: 0.06,
  swayFreq: 3.1,
  swayPhase: 0.7,
  /** Pulled this × its size toward the camera, so the wick or bowl never swallows it. */
  toward: 0.08,
  /** Tongue wobble across the width (full only). */
  tongueAmp: 0.1,
  tongueFreqY: 9,
  tongueSpeed: 11,
  /** Teardrop half-width: (1 − y)^taper · sqrt(clamp(y · baseRound)). */
  taper: 0.85,
  baseRound: 5,
  /** Soft edge: the flame fades out as the width distance d runs edgeStart → 1. */
  edgeStart: 0.5,
  /** Soft tip: the flame fades out as y runs tipStart → 1. */
  tipStart: 0.72,
  /** The white-hot core: full inside d < 0 … coreEdge, fading as y runs coreLow → coreHigh. */
  coreEdge: 0.65,
  coreLow: 0.15,
  coreHigh: 0.8,
  /** Body colour fades to the tip colour as y runs bodyLow → bodyHigh. */
  bodyLow: 0.4,
  bodyHigh: 0.95,
  /** Brightness flicker: flickBase + flickAmp·sin(t·flickFreq + φ·flickPhase), × the second. */
  flickBase: 0.86,
  flickAmp: 0.14,
  flickFreq: 17,
  flickPhase: 3,
  flick2Base: 0.92,
  flick2Amp: 0.08,
  flick2Freq: 5.3,
  /** Fragments dimmer than this are discarded (no fill cost, no stray MRT write). */
  discardBelow: 0.002,
} as const;

/** Point-light look for a lit flame (physical units, three r155+). */
export const FLAME_LIGHT_COLOUR = 0xff9a45;
export const FLAME_LIGHT_INTENSITY = 4;
export const FLAME_LIGHT_DISTANCE = 7;

/** The renderer-specific half: the shared material and its clock/fog inputs. */
export interface FlameMaterialHandle {
  readonly material: Material;
  setTime(seconds: number): void;
  /** Called every frame with the scene's fog (null when none). */
  setFog(fog: Fog | FogExp2 | null): void;
}

/** Builds the material for a detail level — the WebGL2 GLSL one or the WebGPU TSL one. */
export type FlameMaterialFactory = (detail: FlameDetail) => FlameMaterialHandle;

// --- GLSL (WebGL2 default) ----------------------------------------------------

/** A GLSL float literal (`7` ⇒ `7.0`). */
function glf(n: number): string {
  const s = String(n);
  return s.includes('.') || s.includes('e') ? s : `${s}.0`;
}

const S = FLAME_SHAPE;

/** The GLSL vertex stage, generated from `FLAME_SHAPE` (exported for the parity test). */
export const FLAME_VERTEX = /* glsl */ `
uniform float uTime;
uniform float uAspect;
varying vec2 vUv;
varying float vPhase;
#include <common>
#include <fog_pars_vertex>
void main() {
  vUv = uv;
  vec3 originW = (modelMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
  float scale = length(modelMatrix[1].xyz);
  vPhase = fract(sin(dot(originW.xz, vec2(12.9898, 78.233))) * 43758.5453) * 6.2831;
  float h = 1.0 + ${glf(S.heightAmp)} * sin(uTime * ${glf(S.heightFreq)} + vPhase);
#ifndef FLAME_LITE
  h += ${glf(S.heightAmp2)} * sin(uTime * ${glf(S.heightFreq2)} + vPhase * ${glf(S.heightPhase2)});
#endif
  float sway = ${glf(S.swayAmp)} * sin(uTime * ${glf(S.swayFreq)} + vPhase * ${glf(S.swayPhase)}) * uv.y * uv.y;
  vec4 mvPosition = viewMatrix * vec4(originW, 1.0);
  // Toward the camera a little, so the flame is not swallowed by its own wick or bowl.
  mvPosition.xyz += normalize(-mvPosition.xyz) * ${glf(S.toward)} * scale;
  mvPosition.xy += vec2((uv.x - 0.5) * uAspect + sway, uv.y * h) * scale;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

/** The GLSL fragment stage, generated from `FLAME_SHAPE` (exported for the parity test). */
export const FLAME_FRAGMENT = /* glsl */ `
uniform float uTime;
uniform vec3 uCore;
uniform vec3 uBody;
uniform vec3 uTip;
uniform float uIntensity;
varying vec2 vUv;
varying float vPhase;
#include <common>
#include <fog_pars_fragment>
void main() {
  float y = vUv.y;
  float x = (vUv.x - 0.5) * 2.0;
#ifndef FLAME_LITE
  x += ${glf(S.tongueAmp)} * sin(y * ${glf(S.tongueFreqY)} - uTime * ${glf(S.tongueSpeed)} + vPhase) * y;
#endif
  // Teardrop: round at the base, tapering to the tip.
  float halfW = pow(clamp(1.0 - y, 0.0, 1.0), ${glf(S.taper)}) * sqrt(clamp(y * ${glf(S.baseRound)}, 0.0, 1.0));
  float d = abs(x) / max(halfW, 1e-3);
  // Falloffs as 1 - smoothstep(lo, hi, v), lo < hi (reversed edges are UB in GLSL; see FLAME_SHAPE).
  float shape = (1.0 - smoothstep(${glf(S.edgeStart)}, 1.0, d)) * (1.0 - smoothstep(${glf(S.tipStart)}, 1.0, y));
  float core = (1.0 - smoothstep(0.0, ${glf(S.coreEdge)}, d)) * (1.0 - smoothstep(${glf(S.coreLow)}, ${glf(S.coreHigh)}, y));
  float flick = ${glf(S.flickBase)} + ${glf(S.flickAmp)} * sin(uTime * ${glf(S.flickFreq)} + vPhase * ${glf(S.flickPhase)});
#ifndef FLAME_LITE
  flick *= ${glf(S.flick2Base)} + ${glf(S.flick2Amp)} * sin(uTime * ${glf(S.flick2Freq)} + vPhase);
#endif
  vec3 colour = mix(uTip, uBody, 1.0 - smoothstep(${glf(S.bodyLow)}, ${glf(S.bodyHigh)}, y));
  colour = mix(colour, uCore, core);
  colour *= shape * flick * uIntensity;
#ifdef USE_FOG
  #ifdef FOG_EXP2
    float fogFactor = 1.0 - exp(- fogDensity * fogDensity * vFogDepth * vFogDepth);
  #else
    float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
  #endif
  colour *= 1.0 - fogFactor; // additive: fade OUT into fog, never toward its colour
#endif
  if (max(colour.r, max(colour.g, colour.b)) < ${glf(S.discardBelow)}) discard;
  gl_FragColor = vec4(colour, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

/** The WebGL2 flame material (GLSL), shared by every flame of a session. */
export function createFlameShaderMaterial(detail: FlameDetail = 'full'): FlameMaterialHandle {
  const material = new ShaderMaterial({
    name: `flame:${detail}`,
    uniforms: UniformsUtils.merge([
      UniformsLib.fog,
      {
        uTime: { value: 0 },
        uAspect: { value: FLAME_ASPECT },
        uCore: { value: new Color(...FLAME_CORE) },
        uBody: { value: new Color(...FLAME_BODY) },
        uTip: { value: new Color(...FLAME_TIP) },
        uIntensity: { value: FLAME_INTENSITY },
      },
    ]),
    vertexShader: FLAME_VERTEX,
    fragmentShader: FLAME_FRAGMENT,
    defines: detail === 'lite' ? { FLAME_LITE: '' } : {},
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    fog: true, // three refreshes the fog uniforms; the shader fades rather than tints
  });
  const time = material.uniforms['uTime']!;
  return {
    material,
    setTime: (seconds) => {
      time.value = seconds;
    },
    setFog: () => {
      /* WebGLRenderer refreshes fogNear/fogFar/fogDensity itself (material.fog) */
    },
  };
}

// --- The flame object ---------------------------------------------------------

/** The degenerate quad every flame shares: positions at the origin, corners in `uv`. */
export function createFlameGeometry(): BufferGeometry {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(new Float32Array(12), 3));
  geometry.setAttribute('uv', new BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  // The drawn billboard spans ~[-0.3, 0.3] x [0, 1.2] of its scale: cull by a sphere that
  // holds it (the degenerate positions would otherwise give a zero-radius bound).
  geometry.boundingSphere = new Sphere(new Vector3(0, 0.55, 0), 0.75);
  return geometry;
}

/** Anything a flame reports to when it is drawn (the system). */
interface FlameSink {
  noteDrawn(flame: FlameMesh): void;
}

/**
 * One flame. A subclass so a template clone (`clone(true)` ⇒ `new FlameMesh().copy()`)
 * keeps the draw hook and its system link without any per-clone setup.
 */
export class FlameMesh extends Mesh {
  readonly isFlame = true;
  /** The system it reports to (copied by `copy`, so clones of a template report too). */
  sink: FlameSink | null = null;

  constructor(geometry?: BufferGeometry, material?: Material) {
    super(geometry, material);
    this.name = FLAME_OBJECT_NAME;
    this.castShadow = false;
    this.receiveShadow = false;
    this.renderOrder = 10; // after the opaque world; additive, so order among flames is moot
    this.userData['animated'] = true; // staticMerge / scatter instancing keep it live
  }

  /** Lends a point light when `light` was declared (copied through `userData`). */
  get lit(): boolean {
    return this.userData['flameLight'] === true;
  }

  /** The emitter's declared size, metres (copied through `scale`). */
  get size(): number {
    return this.scale.y;
  }

  override copy(source: this, recursive?: boolean): this {
    super.copy(source, recursive);
    this.sink = source.sink;
    return this;
  }

  override raycast(_raycaster: Raycaster, _intersects: Intersection[]): void {
    /* a flame is never picked: clicks go through it to the torch or the NPC */
    void _raycaster;
    void _intersects;
  }

  override onBeforeRender(): void {
    this.sink?.noteDrawn(this);
  }
}

// --- The system -----------------------------------------------------------------

/** Per-frame numbers for `__gcSceneDump` and the lane. */
export interface FlameStats {
  enabled: boolean;
  detail: FlameDetail;
  /** Flames drawn in the last completed frame. */
  drawn: number;
  /** Of those, how many asked for a light. */
  litCandidates: number;
  /** Pool lights given to a flame this frame. */
  lightsLit: number;
  lightCap: number;
}

export interface FlameSystemOptions {
  config: FlameConfig;
  /** The scene the light pool lives in. */
  scene: Object3D;
  /** The material factory for the active renderer (default: the GLSL one). */
  createMaterial?: FlameMaterialFactory;
}

function flickerAt(seconds: number, phase: number): number {
  return (
    0.82 + 0.12 * Math.sin(seconds * 9.1 + phase) + 0.06 * Math.sin(seconds * 23.7 + phase * 2.3)
  );
}

/**
 * Owns the session's flames: their shared geometry and material, their clock, and the
 * point-light pool. `create()` is the emitter factory `installEmitterFactory` takes.
 */
export class FlameSystem implements FlameSink {
  readonly config: FlameConfig;
  readonly geometry: BufferGeometry;
  private readonly handle: FlameMaterialHandle;
  private readonly lights: PointLight[] = [];
  private readonly scene: Object3D;
  private drawnNow = new Set<FlameMesh>();
  private drawnLast = new Set<FlameMesh>();
  private seconds = 0;
  private lightsLit = 0;
  private litCandidates = 0;
  private readonly scratch = new Vector3();
  private readonly cameraPos = new Vector3();

  constructor(options: FlameSystemOptions) {
    this.config = options.config;
    this.scene = options.scene;
    this.geometry = createFlameGeometry();
    this.handle = (options.createMaterial ?? createFlameShaderMaterial)(options.config.detail);
    // Clones of a template borrow these; only `dispose()` frees them (gpuOwnership.ts).
    markSharedResource(this.geometry, this.handle.material);
    for (let i = 0; i < options.config.lights; i += 1) {
      const light = new PointLight(FLAME_LIGHT_COLOUR, 0, FLAME_LIGHT_DISTANCE, 2);
      light.name = `gc-flame-light-${i}`;
      light.castShadow = false;
      this.lights.push(light);
      this.scene.add(light);
    }
  }

  /** The material in use (tests + diagnostics). */
  get material(): Material {
    return this.handle.material;
  }

  /** The emitter factory: one flame object for a declared emitter, or null when off. */
  create(emitter: AssetEmitter): Object3D | null {
    if (!this.config.enabled || emitter.kind !== 'flame') return null;
    const flame = new FlameMesh(this.geometry, this.handle.material);
    flame.scale.setScalar(emitter.size);
    flame.userData['flameLight'] = emitter.light === true;
    flame.sink = this;
    return flame;
  }

  noteDrawn(flame: FlameMesh): void {
    this.drawnNow.add(flame);
  }

  /**
   * Advance one frame, BEFORE render: the clock, the fog inputs, and the light pool —
   * placed on the nearest lit flames drawn in the previous frame.
   */
  update(dtMs: number, camera: Camera, fog: Fog | FogExp2 | null): void {
    this.seconds += Math.max(0, dtMs) / 1000;
    this.handle.setTime(this.seconds);
    this.handle.setFog(fog);
    const last = this.drawnNow;
    this.drawnLast = last;
    this.drawnNow = new Set();
    this.assignLights(last, camera);
  }

  private assignLights(drawn: ReadonlySet<FlameMesh>, camera: Camera): void {
    this.lightsLit = 0;
    this.litCandidates = 0;
    const candidates: { flame: FlameMesh; d2: number }[] = [];
    camera.getWorldPosition(this.cameraPos);
    for (const flame of drawn) {
      if (!flame.lit || flame.parent === null) continue;
      flame.getWorldPosition(this.scratch);
      candidates.push({ flame, d2: this.scratch.distanceToSquared(this.cameraPos) });
    }
    this.litCandidates = candidates.length;
    if (this.lights.length === 0) return;
    candidates.sort((a, b) => a.d2 - b.d2);
    for (let i = 0; i < this.lights.length; i += 1) {
      const light = this.lights[i]!;
      const pick = candidates[i];
      if (pick === undefined) {
        light.intensity = 0;
        continue;
      }
      pick.flame.getWorldPosition(this.scratch);
      const worldSize = pick.flame.getWorldScale(new Vector3()).y;
      light.position.set(this.scratch.x, this.scratch.y + worldSize * 0.4, this.scratch.z);
      const phase = (Math.abs(this.scratch.x * 12.9898 + this.scratch.z * 78.233) % 1) * 6.2831;
      light.intensity = FLAME_LIGHT_INTENSITY * flickerAt(this.seconds, phase);
      this.lightsLit += 1;
    }
  }

  stats(): FlameStats {
    return {
      enabled: this.config.enabled,
      detail: this.config.detail,
      drawn: this.drawnLast.size,
      litCandidates: this.litCandidates,
      lightsLit: this.lightsLit,
      lightCap: this.lights.length,
    };
  }

  dispose(): void {
    for (const light of this.lights) {
      this.scene.remove(light);
      light.dispose();
    }
    this.lights.length = 0;
    this.drawnNow.clear();
    this.drawnLast.clear();
    this.geometry.dispose();
    this.handle.material.dispose();
  }
}
